import React, { useState } from 'react';
import { Share2, Twitter, Facebook, Link, X, MessageCircle, Linkedin, Mail } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ShareModal = ({ isOpen, onClose, contentType, contentId, contentTitle }) => {
  const [shareLinks, setShareLinks] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (isOpen && contentId) {
      fetchShareLinks();
    }
  }, [isOpen, contentId]);

  const fetchShareLinks = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/share/${contentType}/${contentId}`);
      const data = await response.json();
      setShareLinks(data);
    } catch (error) {
      console.error('Failed to get share links:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (shareLinks?.content_url) {
      await navigator.clipboard.writeText(shareLinks.content_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShare = (platform) => {
    if (shareLinks?.platforms?.[platform]) {
      window.open(shareLinks.platforms[platform], '_blank', 'width=600,height=400');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-2xl w-full max-w-md border border-gray-800 overflow-hidden">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-red-600 to-red-800 p-6">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
          <div className="flex items-center gap-3">
            <Share2 className="text-white" size={24} />
            <div>
              <h2 className="text-xl font-bold text-white">Share</h2>
              <p className="text-white/70 text-sm truncate max-w-[250px]">{contentTitle}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8 text-gray-400">Loading share links...</div>
          ) : (
            <>
              {/* Social Platforms */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <ShareButton
                  icon={<Twitter size={24} />}
                  label="Twitter"
                  color="bg-[#1DA1F2]"
                  onClick={() => handleShare('twitter')}
                />
                <ShareButton
                  icon={<Facebook size={24} />}
                  label="Facebook"
                  color="bg-[#4267B2]"
                  onClick={() => handleShare('facebook')}
                />
                <ShareButton
                  icon={<MessageCircle size={24} />}
                  label="WhatsApp"
                  color="bg-[#25D366]"
                  onClick={() => handleShare('whatsapp')}
                />
                <ShareButton
                  icon={<Mail size={24} />}
                  label="Email"
                  color="bg-gray-600"
                  onClick={() => handleShare('email')}
                />
              </div>

              {/* Copy Link */}
              <div className="flex items-center gap-2 bg-gray-800 rounded-lg p-3">
                <input
                  type="text"
                  value={shareLinks?.content_url || ''}
                  readOnly
                  className="flex-1 bg-transparent text-gray-300 text-sm outline-none truncate"
                />
                <button
                  onClick={handleCopyLink}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                    copied
                      ? 'bg-green-600 text-white'
                      : 'bg-red-600 hover:bg-red-700 text-white'
                  }`}
                  data-testid="copy-link-btn"
                >
                  {copied ? 'Copied!' : 'Copy Link'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const ShareButton = ({ icon, label, color, onClick }) => (
  <button
    onClick={onClick}
    className={`${color} hover:opacity-90 text-white p-4 rounded-xl flex flex-col items-center gap-2 transition-all`}
    data-testid={`share-${label.toLowerCase()}-btn`}
  >
    {icon}
    <span className="text-xs">{label}</span>
  </button>
);

// Quick Share Button Component
export const QuickShareButton = ({ contentType, contentId, contentTitle }) => {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
        data-testid="quick-share-btn"
      >
        <Share2 size={18} />
        <span>Share</span>
      </button>
      
      <ShareModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        contentType={contentType}
        contentId={contentId}
        contentTitle={contentTitle}
      />
    </>
  );
};

export default ShareModal;
